import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { createTanzaku } from '../services/api';
import FontSelector from './FontSelector';
import { DEFAULT_FONT } from '../utils/fonts';
import './TanzakuForm.css';

const TanzakuForm = ({ onTanzakuCreated }) => {
  const [formData, setFormData] = useState({
    wish_text: '',
    author_name: '',
    color: 'red',
    font_id: DEFAULT_FONT.id
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const colors = [
    { value: 'red', name: '赤', color: '#ff6b6b' },
    { value: 'blue', name: '青', color: '#4ecdc4' },
    { value: 'yellow', name: '黄', color: '#ffe066' },
    { value: 'green', name: '緑', color: '#6bcf7f' },
    { value: 'purple', name: '紫', color: '#a8678f' },
    { value: 'pink', name: 'ピンク', color: '#ff9ff3' },
    { value: 'orange', name: 'オレンジ', color: '#ff8c69' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleFontChange = (fontId) => {
    setFormData(prev => ({
      ...prev,
      font_id: fontId
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.wish_text.trim() || !formData.author_name.trim()) {
      setError('願い事と名前の両方を入力してください');
      return;
    }

    if (formData.wish_text.length > 100) {
      setError('願い事は100文字以内で入力してください');
      return;
    }

    if (formData.author_name.length > 50) {
      setError('名前は50文字以内で入力してください');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const newTanzaku = await createTanzaku(formData);
      onTanzakuCreated(newTanzaku);
      
      // フォームをリセット
      setFormData({
        wish_text: '',
        author_name: '',
        color: 'red',
        font_id: DEFAULT_FONT.id
      });
      
      // 成功メッセージを表示
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (err) {
      setError(err.message || '短冊の作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div 
      className="tanzaku-form-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="form-header">
        <h2>願い事を書こう</h2>
        <p>あなたの願いを短冊に込めて、笹に飾りましょう</p>
      </div>

      <form onSubmit={handleSubmit} className="tanzaku-form">
        <div className="form-group">
          <label htmlFor="author_name" className="form-label">
            お名前
          </label>
          <input
            type="text"
            id="author_name"
            name="author_name"
            value={formData.author_name}
            onChange={handleInputChange}
            className="form-input"
            placeholder="あなたのお名前を入力してください"
            maxLength={50}
            disabled={isSubmitting}
          />
          <div className="char-count">
            {formData.author_name.length}/50
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="wish_text" className="form-label">
            願い事
          </label>
          <textarea
            id="wish_text"
            name="wish_text"
            value={formData.wish_text}
            onChange={handleInputChange}
            className="form-textarea"
            placeholder="あなたの願い事を入力してください&#10;例：健康で幸せな一年を送れますように"
            maxLength={100}
            rows={4}
            disabled={isSubmitting}
          />
          <div className="char-count">
            {formData.wish_text.length}/100
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            短冊の色
          </label>
          <div className="color-selector">
            {colors.map((colorOption) => (
              <motion.button
                key={colorOption.value}
                type="button"
                className={`color-option ${formData.color === colorOption.value ? 'selected' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, color: colorOption.value }))}
                disabled={isSubmitting}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                style={{ backgroundColor: colorOption.color }}
                title={colorOption.name}
              >
                <span className="color-name">{colorOption.name}</span>
                {formData.color === colorOption.value && (
                  <span className="selected-mark">✓</span>
                )}
              </motion.button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <FontSelector
            selectedFont={formData.font_id}
            onFontChange={handleFontChange}
          />
        </div>

        {error && (
          <motion.div 
            className="error-message"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.div>
        )}

        {showSuccess && (
          <motion.div 
            className="success-message"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            短冊が笹に飾られました！
          </motion.div>
        )}

        <motion.button
          type="submit"
          className="submit-button"
          disabled={isSubmitting || !formData.wish_text.trim() || !formData.author_name.trim()}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {isSubmitting ? (
            <span className="loading-spinner">
              <span className="spinner"></span>
              短冊を作成中...
            </span>
          ) : (
            '短冊にする'
          )}
        </motion.button>
      </form>
    </motion.div>
  );
};

export default TanzakuForm;